import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import * as nspell from 'nspell';

export interface SpellCheckResult {
  word: string;
  isCorrect: boolean;
  suggestions: string[];
  position: { start: number; end: number };
}

@Injectable({
  providedIn: 'root'
})
export class SpellCheckerService {
  private spellChecker: any = null;
  private isDictionaryLoaded = false;
  private loadingPromise: Promise<void> | null = null;

  constructor(private http: HttpClient) {
    this.loadDictionary();
  }

  /**
   * Load dictionary files from assets
   */
  private async loadDictionary(): Promise<void> {
    if (this.isDictionaryLoaded) {
      return Promise.resolve();
    }

    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = (async () => {
      try {
        // Try to load dictionary files
        // Using US English dictionary - you can change this to other languages
        const affPromise = this.http.get('assets/dictionaries/en_US.aff', { responseType: 'text' }).toPromise();
        const dicPromise = this.http.get('assets/dictionaries/en_US.dic', { responseType: 'text' }).toPromise();

        const [aff, dic] = await Promise.all([affPromise, dicPromise]);
        
        // Initialize nspell with the dictionary
        this.spellChecker = nspell(aff, dic);
        this.isDictionaryLoaded = true;
      } catch (error) {
        console.warn('Failed to load dictionary files. Spell checking will be disabled.', error);
        // Create a minimal spell checker that accepts all words
        this.spellChecker = {
          correct: () => true,
          suggest: () => []
        };
        this.isDictionaryLoaded = true;
      }
    })();

    return this.loadingPromise;
  }

  /**
   * Check if a word is spelled correctly
   */
  async checkWord(word: string): Promise<boolean> {
    await this.loadDictionary();
    if (!this.spellChecker) {
      return true; // If no dictionary, assume correct
    }
    
    // Clean the word (remove punctuation, etc.)
    const cleanWord = this.cleanWord(word);
    if (!cleanWord || cleanWord.length === 0) {
      return true;
    }
    
    return this.spellChecker.correct(cleanWord);
  }

  /**
   * Get suggestions for a misspelled word
   */
  async getSuggestions(word: string): Promise<string[]> {
    await this.loadDictionary();
    if (!this.spellChecker) {
      return [];
    }
    
    const cleanWord = this.cleanWord(word);
    if (!cleanWord || cleanWord.length === 0) {
      return [];
    }
    
    const suggestions = this.spellChecker.suggest(cleanWord);
    return suggestions.slice(0, 5); // Return top 5 suggestions
  }

  /**
   * Check spelling of all words in a text
   */
  async checkText(text: string): Promise<SpellCheckResult[]> {
    await this.loadDictionary();
    if (!text || text.trim().length === 0) {
      return [];
    }

    const results: SpellCheckResult[] = [];
    const words = this.extractWords(text);
    
    let currentPosition = 0;
    
    for (const wordInfo of words) {
      const { word, start, end } = wordInfo;
      const cleanWord = this.cleanWord(word);
      
      if (cleanWord && cleanWord.length > 0) {
        const isCorrect = this.spellChecker ? this.spellChecker.correct(cleanWord) : true;
        
        if (!isCorrect) {
          const suggestions = this.spellChecker ? this.spellChecker.suggest(cleanWord).slice(0, 5) : [];
          results.push({
            word: word,
            isCorrect: false,
            suggestions: suggestions,
            position: { start, end }
          });
        }
      }
      
      currentPosition = end;
    }
    
    return results;
  }

  /**
   * Extract words from text with their positions
   */
  private extractWords(text: string): Array<{ word: string; start: number; end: number }> {
    const words: Array<{ word: string; start: number; end: number }> = [];
    const wordRegex = /\b\w+\b/g;
    let match;
    
    while ((match = wordRegex.exec(text)) !== null) {
      words.push({
        word: match[0],
        start: match.index,
        end: match.index + match[0].length
      });
    }
    
    return words;
  }

  /**
   * Clean word - remove special characters but keep the original for display
   */
  private cleanWord(word: string): string {
    return word.replace(/[^\w]/g, '').toLowerCase();
  }

  /**
   * Add a word to the personal dictionary
   */
  async addToDictionary(word: string): Promise<void> {
    await this.loadDictionary();
    if (this.spellChecker && this.spellChecker.add) {
      this.spellChecker.add(word);
    }
  }

  /**
   * Check if dictionary is loaded
   */
  isReady(): boolean {
    return this.isDictionaryLoaded;
  }
}

